/* Shopify is the source of truth. Never calculate or apply preview discounts here. */
(() => {
  if (customElements.get('cl-cart-progress-b')) return;
  let mutationPending = false;
  customElements.define('cl-cart-progress-b', class extends HTMLElement {
    connectedCallback() {
      if (this.events) return;
      this.events = new AbortController();
      const opts = { signal: this.events.signal };
      this.querySelectorAll('[data-add-variant]').forEach(button => { button.disabled = false; });
      this.addEventListener('change', event => {
        const input = event.target.closest('[data-quantity]');
        if (!input) return;
        if (!input.reportValidity() || !Number.isInteger(Number(input.value))) {
          input.value = input.dataset.original;
          return;
        }
        this.mutate('change', { id: input.closest('[data-cart-line]').dataset.cartLine, quantity: Number(input.value) }, input);
      }, opts);
      this.addEventListener('click', event => {
        const remove = event.target.closest('[data-remove]');
        const add = event.target.closest('[data-add-variant]');
        if (remove) {
          event.preventDefault();
          this.mutate('change', { id: remove.closest('[data-cart-line]').dataset.cartLine, quantity: 0 }, remove);
        } else if (add) {
          event.preventDefault();
          this.mutate('add', { items: [{ id: Number(add.dataset.addVariant), quantity: Number(add.dataset.addQuantity) || 1 }] }, add);
        }
      }, opts);
      this.addEventListener('submit', event => { if (mutationPending) event.preventDefault(); }, opts);
      // Avoid a stale cart after returning from checkout with the browser back button.
      window.addEventListener('pageshow', event => { if (event.persisted) location.reload(); }, opts);
    }
    disconnectedCallback() {
      this.events?.abort();
      this.events = null;
    }
    get viewURL() {
      const url = new URL(this.dataset.cartUrl, location.origin);
      url.searchParams.set('view', 'clutchloop-b');
      return url;
    }
    setBusy(busy) {
      this.setAttribute('aria-busy', String(busy));
      if (busy) {
        this.disabledBefore = new Map();
        this.querySelectorAll('button,input,select').forEach(el => {
          this.disabledBefore.set(el, el.disabled);
          el.disabled = true;
        });
      } else {
        this.disabledBefore?.forEach((disabled, el) => { el.disabled = disabled; });
      }
    }
    showError(message) {
      const error = this.querySelector('[data-error]');
      error.textContent = message;
      error.hidden = false;
      error.focus();
    }
    async request(url, options = {}) {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 15000);
      try {
        return await fetch(url, { credentials: 'same-origin', cache: 'no-store', ...options, signal: controller.signal });
      } finally { clearTimeout(timeout); }
    }
    async mutate(action, body, control) {
      if (mutationPending) return;
      mutationPending = true;
      this.setBusy(true);
      this.querySelector('[data-error]').hidden = true;
      const lineKey = control.closest('[data-cart-line]')?.dataset.cartLine;
      const isQuantity = control.hasAttribute('data-quantity');
      let responseKnown = false;
      let committed = false;
      let navigating = false;
      try {
        const root = this.dataset.root.replace(/\/?$/, '/');
        const view = this.viewURL;
        const response = await this.request(`${root}cart/${action}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ ...body, sections: [this.dataset.sectionId, 'cart-icon-bubble'], sections_url: view.pathname + view.search })
        });
        responseKnown = true;
        committed = response.ok;
        const data = await response.json();
        if (!response.ok) throw new Error(typeof data.description === 'string' ? data.description : 'Shopify could not update your cart. Check availability and try again.');
        let html = data.sections?.[this.dataset.sectionId];
        if (!html) {
          const refreshURL = this.viewURL;
          refreshURL.searchParams.set('section_id', this.dataset.sectionId);
          const fresh = await this.request(refreshURL);
          if (!fresh.ok) throw new Error('The cart was updated but could not be displayed.');
          html = await fresh.text();
        }
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const updated = parsed.querySelector('cl-cart-progress-b');
        if (!updated) throw new Error('The cart section could not be displayed.');
        const bubble = document.getElementById('cart-icon-bubble');
        const bubbleHTML = data.sections?.['cart-icon-bubble'];
        if (bubble && bubbleHTML) {
          const doc = new DOMParser().parseFromString(bubbleHTML, 'text/html');
          bubble.innerHTML = (doc.getElementById('cart-icon-bubble') || doc.querySelector('.shopify-section') || doc.body).innerHTML;
        }
        this.replaceWith(updated);
        const matchingRow = [...updated.querySelectorAll('[data-cart-line]')].find(row => row.dataset.cartLine === lineKey);
        const target = matchingRow?.querySelector(isQuantity ? '[data-quantity]' : '[data-remove]') || updated.querySelector('[data-quantity],.cbt-empty a');
        target?.focus({ preventScroll: true });
        let announcement = document.getElementById('cl-cart-b-announcement');
        if (!announcement) {
          announcement = document.createElement('div');
          announcement.id = 'cl-cart-b-announcement';
          announcement.className = 'cbt-sr-only';
          announcement.setAttribute('role', 'status');
          document.body.appendChild(announcement);
        }
        announcement.textContent = 'Cart updated. ' + (updated.querySelector('.cbt-total')?.textContent || 'Your bag is empty.');
        document.dispatchEvent(new CustomEvent('clutchloop:cart-updated', { detail: { source: 'clutchloop-cart-b' } }));
      } catch (error) {
        if (committed || !responseKnown) {
          // The request might have succeeded. Re-read the cart, never replay an add.
          navigating = true;
          location.assign(this.viewURL.href);
          return;
        }
        this.querySelectorAll('[data-quantity]').forEach(input => { input.value = input.dataset.original; });
        this.showError(error.message || 'Unable to update your cart. Please try again.');
      } finally {
        if (!navigating) {
          mutationPending = false;
          this.setBusy(false);
        }
      }
    }
  });
})();
